/**
 * Spoofio Configuration Constants
 *
 * ALL configurable values must be defined here.
 * Import from '@spoofio/shared/config' to use these constants.
 */

// ============================================================================
// Domain Configuration
// Note: These are defaults. Actual values come from Cloudflare Worker bindings.
// ============================================================================

export const DEFAULT_DOMAIN_NAME = 'cdn.stylerender.net'

// ============================================================================
// API Versioning
// ============================================================================

export const API_VERSION = 'v1'
export const API_BASE_PATH = `/api/${API_VERSION}`

// ============================================================================
// Encryption
// ============================================================================

export const ENCRYPTION_KEY_VERSION = 'v1'

/**
 * Build API base URL from domain name
 */
export function buildApiBaseUrl(domainName: string): string {
  return `https://${domainName}${API_BASE_PATH}`
}

/**
 * Build pixel base URL from domain name
 */
export function buildPixelBaseUrl(domainName: string): string {
  return `https://${domainName}/c`
}

// ============================================================================
// Authentication
// ============================================================================

export const JWT_EXPIRATION_SECONDS = 60 * 60 // 1 hour

// ============================================================================
// Rate Limiting
// ============================================================================

export const RATE_LIMIT_PER_IP = 100 // requests per minute
export const RATE_LIMIT_PER_CANARY = 1000 // requests per minute
export const RATE_LIMIT_GLOBAL = 50000 // requests per minute
export const RATE_LIMIT_WINDOW_SECONDS = 60 // 1 minute sliding window

// ============================================================================
// Detection & Scoring
// ============================================================================

export const DEFAULT_CONFIDENCE_THRESHOLD = 70
export const MIN_CONFIDENCE_THRESHOLD = 0
export const MAX_CONFIDENCE_THRESHOLD = 100

// Scoring weights - must sum to 1.0
export const DOMAIN_SIMILARITY_WEIGHT = 0.6
export const REFERRER_WEIGHT = 0.2
export const GEO_WEIGHT = 0.2

// Levenshtein distance thresholds for domain similarity
export const DOMAIN_EXACT_MATCH_SCORE = 0 // Not a fake domain
export const DOMAIN_HIGH_SIMILARITY_THRESHOLD = 3 // Very similar (typosquatting)
export const DOMAIN_MEDIUM_SIMILARITY_THRESHOLD = 6 // Somewhat similar

// ============================================================================
// Alerting
// ============================================================================

export const ALERT_DEDUP_WINDOW_SECONDS = 60 * 60 // 1 hour
export const DAILY_ALERT_CAP = 10 // unique domains per canary per 24h
export const ALERT_RETRY_ATTEMPTS = 3
export const ALERT_RETRY_DELAY_MS = 5000

// ============================================================================
// Integration Error Logging
// ============================================================================

export const INTEGRATION_ERROR_RETENTION_DAYS = 30

// ============================================================================
// Caching
// ============================================================================

export const CANARY_CONFIG_CACHE_TTL_SECONDS = 300 // 5 minutes
export const RATE_LIMIT_CACHE_TTL_SECONDS = 60 // 1 minute

// ============================================================================
// Pagination
// ============================================================================

export const DEFAULT_PAGE_SIZE = 25
export const MAX_PAGE_SIZE = 100

// ============================================================================
// Validation Limits
// ============================================================================

export const MAX_CANARY_NAME_LENGTH = 100
export const MAX_CANARY_DESCRIPTION_LENGTH = 500
export const MAX_AUTHORIZED_DOMAINS_PER_CANARY = 50
export const MAX_RECIPIENTS_PER_ORGANIZATION = 20
export const MAX_RECIPIENTS_PER_CANARY = 10

// ============================================================================
// Pixel Configuration (Legacy)
// ============================================================================

export const PIXEL_PATH_PREFIX = '/c/'
export const PIXEL_EXTENSION = '.png'
export const PIXEL_TOKEN_REGEX = /^[a-zA-Z0-9_-]+$/

// 1x1 transparent PNG as base64
export const TRANSPARENT_PIXEL_BASE64 =
  'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=='

// ============================================================================
// Component Mask Detection Configuration
// CSS/JS asset-based detection for improved stealth
// ============================================================================

/** Detection endpoint domains */
export const DETECTION_DOMAINS = {
  /** Production CDN domain (unbranded, appears as generic CDN) */
  PRODUCTION: 'https://cdn.stylerender.net',
  /** Staging CDN domain (unbranded obfuscation host, mirrors production) */
  STAGING: 'https://staging-cdn.stylerender.net',
  /** Local development endpoint */
  LOCAL: 'http://localhost:8788',
} as const

/** Supported asset types for Component Mask detection */
export const DETECTION_ASSET_TYPES = ['css', 'js'] as const

/** Query parameter names for Component Mask URLs */
export const DETECTION_PARAMS = {
  /** Encoded canary token (looks like content hash) */
  TOKEN: 'h',
  /** Encoded source URL from JS embed */
  SOURCE: 'r',
  /** Decoy version number */
  VERSION: 'v',
  /** Decoy cache buster */
  CACHE_BUST: 'cb',
  /** Bootstrap mode flag — returns source-capture script instead of benign payload */
  BOOT: 'b',
} as const

/** Prefix for encoded tokens to make them look like content hashes */
export const TOKEN_ENCODING_PREFIX = 'w'

/** Decoy version strings rotated for variation */
export const DECOY_VERSIONS = [
  '2.4.0',
  '2.4.1',
  '2.5.0',
  '3.0.0',
  '3.1.2',
  '1.9.8',
  '2.0.0',
] as const

/** Content-Type headers for asset responses */
export const ASSET_CONTENT_TYPES = {
  css: 'text/css; charset=utf-8',
  js: 'application/javascript; charset=utf-8',
} as const

/** Default payload configuration for Component Mask assets */
export const DEFAULT_PAYLOAD_CONFIG = {
  productName: 'Embed Widget',
  version: '2.4.0',
  author: 'Platform Inc.',
  classPrefix: 'widget',
} as const

// ============================================================================
// Email Sender Identity
// The From address every Email Provider sends as, unless a message overrides it.
// Provider-agnostic: both the SendGrid and Cloudflare transports read these.
// ============================================================================

export const DEFAULT_FROM_EMAIL = 'alerts@spoofio.com'
export const EMAIL_FROM_NAME = 'Spoofio Alerts'

// ============================================================================
// SendGrid Configuration
// Note: Actual values come from Cloudflare Worker secrets.
// ============================================================================

export const SENDGRID_API_URL = 'https://api.sendgrid.com/v3/mail/send'

// ============================================================================
// Email Provider Configuration
// Cloudflare Email is the platform default. SendGrid is retained as a selectable
// fallback so re-enabling it is a setting change, not a code change. The Active
// Email Provider is resolved per send:
//   system_settings('email_provider') > EMAIL_PROVIDER env > DEFAULT_EMAIL_PROVIDER.
// ============================================================================

/**
 * Supported email transport providers. This tuple is the single source of truth
 * for the provider identity — the `EmailProviderName` union (types/api.ts) and the
 * `emailProviderSchema` Zod validator (validation/schemas.ts) are derived from it.
 */
export const EMAIL_PROVIDERS = ['sendgrid', 'cloudflare'] as const

/** Provider used when EMAIL_PROVIDER is unset or fails validation. */
export const DEFAULT_EMAIL_PROVIDER = 'cloudflare' as const

/**
 * Keys for the generic system_settings table. Single source of truth for the
 * setting identifiers used by the GUI-managed Platform settings (e.g. the active
 * email provider). The table is generic key/value; the HTTP surface stays typed
 * per setting.
 */
export const SYSTEM_SETTING_KEYS = {
  EMAIL_PROVIDER: 'email_provider',
} as const

/**
 * Cloudflare Email Sending REST endpoint for a given account.
 * @see https://developers.cloudflare.com/email-service/
 */
export const CLOUDFLARE_EMAIL_SEND_URL = (accountId: string): string =>
  `https://api.cloudflare.com/client/v4/accounts/${accountId}/email/sending/send`

/**
 * Cloudflare Email Sending limits endpoint. Probed to validate that the
 * CLOUDFLARE_EMAIL_API_TOKEN + CF_ACCOUNT_ID pair is healthy (Infrastructure Health).
 */
export const CLOUDFLARE_EMAIL_LIMITS_URL = (accountId: string): string =>
  `https://api.cloudflare.com/client/v4/accounts/${accountId}/email/sending/limits`
export const EMAIL_SUBJECTS = {
  testEmail: () => '[Spoofio] Test Email - Infrastructure Health Check',
  welcome: (orgName: string) => `Welcome to Spoofio — ${orgName}`,
  detectionAlert: (fakeFqdn: string) => `[Spoofio Alert] Unauthorized access detected: ${fakeFqdn}`,
  dailySummary: (count: number, canaryName: string) => `[Spoofio Summary] ${count} detections for ${canaryName}`,
  paymentFailedFinal: (orgName: string) => `Action Required: Payment failed for ${orgName}`,
  paymentRecovered: (orgName: string) => `Payment recovered for ${orgName}`,
  subscriptionCanceled: (orgName: string) => `Subscription canceled for ${orgName}`,
  reactivationConfirmed: (orgName: string) => `Welcome back! ${orgName} has been reactivated`,
} as const

// ============================================================================
// Screenshot Configuration
// ============================================================================

/** Screenshot capture settings */
export const SCREENSHOT_CONFIG = {
  /** Viewport width in pixels */
  VIEWPORT_WIDTH: 1280,
  /** Viewport height in pixels */
  VIEWPORT_HEIGHT: 800,
  /** Navigation timeout in milliseconds (15 seconds) */
  NAVIGATION_TIMEOUT_MS: 15000,
  /** Wait time after page load in milliseconds (2 seconds) */
  WAIT_AFTER_LOAD_MS: 2000,
  /** Screenshot image format (puppeteer type) */
  FORMAT: 'jpeg' as const,
  /** JPEG quality (0-100) */
  QUALITY: 80,
  /** R2 key file extension */
  FILE_EXTENSION: '.jpg',
  /** HTTP Content-Type for screenshot responses */
  CONTENT_TYPE: 'image/jpeg',
  /** Capture the full page, not just the viewport */
  FULL_PAGE: true,
  /**
   * Capture the full page by resizing the viewport to the document height and
   * taking a single in-place shot (`fullPage: false`), instead of relying on
   * Puppeteer's `fullPage: true`. Puppeteer's built-in full-page mode re-scrolls
   * and re-resizes the page at screenshot time, which re-fires the site's own
   * scroll/resize handlers AFTER sticky neutralization runs — re-pinning a nav
   * partway down the page. The manual path makes neutralization the last thing
   * to touch the DOM before capture, eliminating that race.
   */
  MANUAL_FULL_PAGE_CAPTURE: true as boolean,
  /**
   * Upper bound (px) for the manual full-page viewport height. Chrome cannot
   * paint an unbounded surface; extremely tall pages are clamped to this height
   * to avoid a failed/blank capture. Tracks Chrome's practical screenshot limit.
   */
  MAX_FULL_PAGE_HEIGHT_PX: 16384,
  /** Delay between scroll steps in milliseconds (for triggering lazy content) */
  SCROLL_STEP_DELAY_MS: 200,
  /** Wait after the final layout mutation before capturing, so reflow settles */
  LAYOUT_SETTLE_MS: 150,
  /**
   * Inject a stylesheet that disables transitions, animations, and smooth
   * scrolling before capture. Stops mid-animation/keyframe artifacts and
   * scroll-anchoring from skewing the shot.
   */
  DISABLE_ANIMATIONS: true as boolean,
  /**
   * Scroll back to the top after the lazy-load scroll, before capturing.
   * Prevents a sticky/fixed nav that became "stuck" mid-scroll from being
   * painted floating in the middle of the full-page Screenshot.
   */
  RESET_SCROLL_BEFORE_CAPTURE: true as boolean,
  /**
   * Demote `position: fixed`/`position: sticky` elements to `static` before
   * capturing, so a sticky header is rendered once at the top instead of
   * repeated per stitched section on tall pages. Targeted (only stuck
   * elements) to preserve the forensic accuracy of how the fake page looked.
   */
  NEUTRALIZE_STICKY: true as boolean,
  /** Maximum retry attempts */
  MAX_RETRIES: 2,
} as const

// ============================================================================
// GELF/Graylog Configuration
// ============================================================================

/** GELF syslog severity levels */
export const GELF_SEVERITY_LEVELS = {
  EMERGENCY: 0,
  ALERT: 1,
  CRITICAL: 2,
  ERROR: 3,
  WARNING: 4,
  NOTICE: 5,
  INFO: 6,
  DEBUG: 7,
} as const

/** Confidence score thresholds for GELF severity mapping */
export const GELF_SEVERITY_THRESHOLDS = {
  /** Score >= this is CRITICAL severity */
  CRITICAL: 90,
  /** Score >= this is WARNING severity */
  WARNING: 70,
} as const

/** Host identifier for GELF messages */
export const GELF_HOST = 'spoofio'

// ============================================================================
// UI Configuration
// ============================================================================

/** Query client configuration for React Query */
export const QUERY_CLIENT_CONFIG = {
  /** Stale time in milliseconds (5 minutes) */
  STALE_TIME_MS: 5 * 60 * 1000,
  /** Number of retry attempts */
  RETRY_COUNT: 1,
} as const

/** Copy to clipboard notification timeout in milliseconds */
export const COPY_NOTIFICATION_TIMEOUT_MS = 2000

/** Negative cache TTL for canary lookups in seconds (1 minute) */
export const CANARY_NEGATIVE_CACHE_TTL_SECONDS = 60

// ============================================================================
// CORS Configuration
// ============================================================================

/** Allowed origins for CORS */
export const CORS_ALLOWED_ORIGINS = {
  /** Local development UI (Vite dev server) */
  LOCAL_VITE: 'http://localhost:5173',
  /** Local development UI (preview/build) */
  LOCAL_PREVIEW: 'http://localhost:3000',
  /** Cloudflare Pages default domain */
  PAGES_DEV: 'https://spoofio-admin.pages.dev',
  /** Staging Cloudflare Pages domain */
  STAGING_PAGES: 'https://spoofio-admin-staging.pages.dev',
  /** Staging custom domain for Admin UI */
  STAGING_CUSTOM: 'https://staging.spoofio.com',
  /** Production custom domain for Admin UI */
  PRODUCTION: 'https://app.spoofio.com',
} as const

/** Allowed HTTP methods for CORS */
export const CORS_ALLOWED_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'] as const

/** Allowed headers for CORS */
export const CORS_ALLOWED_HEADERS = ['Content-Type', 'Authorization', 'X-API-Key'] as const

/** CORS preflight cache max-age in seconds (24 hours) */
export const CORS_MAX_AGE_SECONDS = 86400

// ============================================================================
// Global Allowlist Patterns
// ============================================================================

export const ALLOWLIST_REFERRER_PATTERNS = [
  /^https?:\/\/web\.archive\.org\//i,
  /^https?:\/\/.*\.googleusercontent\.com/i,
  /^https?:\/\/safebrowsing\.google\.com/i,
  /^https?:\/\/transparencyreport\.google\.com/i,
  /^https?:\/\/localhost/i,
  /^https?:\/\/127\.0\.0\.1/i,
  /^https?:\/\/\[::1\]/i,
]

export const ALLOWLIST_USER_AGENT_PATTERNS = [
  /Googlebot/i,
  /Bingbot/i,
  /baiduspider/i,
  /YandexBot/i,
  /DuckDuckBot/i,
  /facebookexternalhit/i,
  /Twitterbot/i,
  /LinkedInBot/i,
  /WhatsApp/i,
  /Slackbot/i,
  /TelegramBot/i,
  /Discordbot/i,
  /Slurp/i, // Yahoo
  /archive\.org_bot/i,
  /ia_archiver/i,
]

export const DEVELOPMENT_DOMAINS = [
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '::1',
]

// ============================================================================
// HTTP Headers
// ============================================================================

export const CF_CONNECTING_IP_HEADER = 'cf-connecting-ip'
export const CF_RAY_HEADER = 'cf-ray'
export const CF_IPCOUNTRY_HEADER = 'cf-ipcountry'
export const CF_CITY_HEADER = 'cf-city'
export const CF_REGION_HEADER = 'cf-region'
export const X_FORWARDED_FOR_HEADER = 'x-forwarded-for'
export const X_REAL_IP_HEADER = 'x-real-ip'

// ============================================================================
// Cache Keys
// ============================================================================

export const CACHE_KEY_CANARY_PREFIX = 'canary:'
export const CACHE_KEY_RATE_LIMIT_IP_PREFIX = 'rate:ip:'
export const CACHE_KEY_RATE_LIMIT_CANARY_PREFIX = 'rate:canary:'
export const CACHE_KEY_RATE_LIMIT_GLOBAL = 'rate:global'
export const CACHE_KEY_ALERT_DEDUP_PREFIX = 'alert:'
export const CACHE_KEY_DAILY_ALERT_COUNT_PREFIX = 'daily:'
export const CACHE_KEY_PLAN_PREFIX = 'plan:'
export const CACHE_KEY_RADAR_HEARTBEAT = 'ffiec:radar:heartbeat'

/** How long a Radar heartbeat entry lives in ALERT_DEDUP KV (48 hours) */
export const RADAR_HEARTBEAT_TTL_SECONDS = 48 * 60 * 60

/** After this many ms without a successful Radar scan, status becomes degraded (4 hours) */
export const RADAR_HEARTBEAT_STALE_MS = 4 * 60 * 60 * 1000

// ============================================================================
// Billing Configuration
// ============================================================================

/** Subscription pricing in cents (USD) */
export const BILLING_PRICES = {
  /** Base plan monthly price: $99/mo */
  BASE_PLAN_MONTHLY_CENTS: 9900,
  /** Additional domain monthly price: $99/mo each */
  ADDITIONAL_DOMAIN_MONTHLY_CENTS: 9900,
  /** Additional canary monthly price: $20/mo each */
  ADDITIONAL_CANARY_MONTHLY_CENTS: 2000,
  /** Request overage price: $0.005/request */
  REQUEST_OVERAGE_CENTS: 0.5,
  /** FFIEC Enhanced Detection monthly price: $50/mo per canary */
  FFIEC_ENHANCED_MONTHLY_CENTS: 5000,
} as const

/** Hard cap on total FFIEC enrichment time in milliseconds (45 seconds).
 *  Alert sends unchanged if enrichment exceeds this budget. */
export const FFIEC_ENRICHMENT_TIMEOUT_MS = 45_000

/** Plan limits */
export const BILLING_LIMITS = {
  /** Domains included in base plan */
  BASE_DOMAIN_LIMIT: 1,
  /** Canaries included in base plan */
  BASE_CANARY_LIMIT: 5,
  /** Daily requests per canary included in base plan */
  BASE_DAILY_REQUEST_LIMIT: 10000,
  /** Domains included in trial */
  TRIAL_DOMAIN_LIMIT: 1,
  /** Canaries included in trial */
  TRIAL_CANARY_LIMIT: 1,
  /** Daily requests per canary during trial */
  TRIAL_DAILY_REQUEST_LIMIT: 10000,
} as const

/** Trial configuration */
export const TRIAL_DURATION_DAYS = 7

/** Account lifecycle */
export const DELETION_GRACE_PERIOD_DAYS = 30
export const MAX_PAYMENT_FAILURE_COUNT = 3

/** Per-organization rate limit (requests per minute across all canaries) */
export const RATE_LIMIT_PER_ORG = 5000

// ============================================================================
// Admin API Rate Limiting
// ============================================================================

/** Per-org rate limit for Admin API (requests per minute) */
export const ADMIN_API_RATE_LIMIT_PER_ORG = 200

/** Per-IP rate limit for unauthenticated Admin API requests (requests per minute) */
export const ADMIN_API_RATE_LIMIT_PER_IP = 30

/** Admin API rate limit window in seconds */
export const ADMIN_API_RATE_LIMIT_WINDOW_SECONDS = 60

// ============================================================================
// Business-Flow Throttling (sensitive operations)
// ============================================================================

/** Canary creation: max per org per hour */
export const THROTTLE_CANARY_CREATION_PER_HOUR = 10

/** Member invitation: max per org per day */
export const THROTTLE_MEMBER_INVITATION_PER_DAY = 20

/** API key regeneration: max per org per hour */
export const THROTTLE_API_KEY_REGEN_PER_HOUR = 5

/** Test email: max per org per hour */
export const THROTTLE_TEST_EMAIL_PER_HOUR = 5

// ============================================================================
// Admin API Rate Limit Cache Keys
// ============================================================================

export const CACHE_KEY_ADMIN_RATE_LIMIT_ORG_PREFIX = 'admin:rate:org:'
export const CACHE_KEY_ADMIN_RATE_LIMIT_IP_PREFIX = 'admin:rate:ip:'
export const CACHE_KEY_THROTTLE_PREFIX = 'throttle:'

/** Plan cache TTL in seconds */
export const PLAN_CACHE_TTL_SECONDS = 300

// ============================================================================
// Terms of Service
// ============================================================================

export const CURRENT_TERMS_VERSION = '1.0'

// ============================================================================
// Stripe Configuration
// ============================================================================

export const STRIPE_API_VERSION = '2024-12-18.acacia'
export const STRIPE_API_BASE_URL = 'https://api.stripe.com/v1'

/** Stripe webhook tolerance window in seconds (5 minutes) */
export const STRIPE_WEBHOOK_TOLERANCE_SECONDS = 300

// ============================================================================
// Clerk Configuration
// ============================================================================

/** JWKS cache TTL in seconds (1 hour) */
export const CLERK_JWKS_CACHE_TTL_SECONDS = 3600

/** KV key for cached JWKS */
export const CLERK_JWKS_CACHE_KEY = 'clerk:jwks'

/** Maximum clock skew tolerance for JWT validation in seconds */
export const CLERK_JWT_CLOCK_SKEW_SECONDS = 60

/** Svix webhook timestamp tolerance in seconds (5 minutes) */
export const CLERK_WEBHOOK_TOLERANCE_SECONDS = 300
